import { useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';

import { Button, Card, MonoLabel, cn, focusRing } from '@/design-system';
import {
    BRAND_ACCENTS,
    defaultTextOnAccent,
    effectiveTextOnAccent,
    isAcceptedLogo,
    LOGO_MIME_TYPES,
    ON_ACCENT_DARK,
    ON_ACCENT_LIGHT,
    toHouseStyle,
    type Branding,
    type HouseStyle,
} from '@/entities/tenant';
import { contrastRatio, isReadable, parseColor } from '@/shared/lib';

import { brandingCopy, settingsCopy } from '../copy';

export interface BrandingCardProps {
    branding: Branding;
    companyName: string;
    /** Alleen de eigenaar past de huisstijl aan. */
    canManage: boolean;
    /** De lokale voorvertoning wint van `branding.logoUrl` tot de upload rond is. */
    logoPreview: string | null;
    onLogoSelected: (file: File) => void;
    onLogoRemove: () => void;
    /** Elke wijziging, zodat de voorvertoning ernaast meteen meebeweegt. */
    onHouseStyleChange: (style: HouseStyle) => void;
    onHouseStyleSave: (style: HouseStyle) => void;
    uploading?: boolean;
    savingHouseStyle?: boolean;
    notice?: string | null;
    error?: string | null;
}

/**
 * Huisstijl in rapporten (scherm 11, linkerkolom).
 *
 * Drie kleuren, en niet meer: de accentkleur, de tekst die erop staat en een
 * rustige tweede kleur voor koppen. Ze kleuren het **rapport, de e-mails en de
 * klantpagina**, nooit de app-chrome: de app blijft Smartkeur-groen
 * (patterns.md §10). De vier swatches zijn startpunten; daarnaast staat een
 * kleurkiezer met het volledige spectrum en een veld voor een eigen HEX- of
 * RGB-code.
 *
 * Twee dingen veranderen niet mee. De statuskleuren van de keuring en de amber
 * van offline zeggen *wat iets is* en blijven bij elk bedrijf gelijk. En de
 * leesbaarheid: bij elke combinatie staat de gemeten contrastverhouding, en
 * zakt die onder 4,5:1 dan waarschuwt het scherm en ligt de leesbare kleur één
 * klik verderop. Sla je het toch zo op, dan drukt de API alsnog een leesbare
 * kleur af — een rapport wordt maanden later gerenderd, zonder iemand erbij.
 *
 * Het logoveld neemt alleen aan wat de API als logo aanneemt: PNG, JPG en WebP.
 */
export function BrandingCard({
    branding,
    companyName,
    canManage,
    logoPreview,
    onLogoSelected,
    onLogoRemove,
    onHouseStyleChange,
    onHouseStyleSave,
    uploading = false,
    savingHouseStyle = false,
    notice = null,
    error = null,
}: BrandingCardProps) {
    const [dragging, setDragging] = useState(false);
    const [typeError, setTypeError] = useState<string | null>(null);
    const [saved, setSaved] = useState<HouseStyle>(() => toHouseStyle(branding));
    const [draft, setDraft] = useState<HouseStyle>(() => toHouseStyle(branding));
    const logoUrl = logoPreview ?? branding.logoUrl;
    const editable = canManage;
    const dirty =
        draft.accent !== saved.accent ||
        draft.accentText !== saved.accentText ||
        draft.heading !== saved.heading;

    function change(next: HouseStyle) {
        setDraft(next);
        onHouseStyleChange(next);
    }

    function take(file: File | undefined) {
        if (!file) {
            return;
        }

        if (!isAcceptedLogo(file)) {
            setTypeError(brandingCopy.logoWrongType);

            return;
        }

        setTypeError(null);
        onLogoSelected(file);
    }

    function handleDrop(event: DragEvent<HTMLLabelElement>) {
        event.preventDefault();
        setDragging(false);
        take(event.dataTransfer.files[0]);
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        take(event.target.files?.[0]);
        event.target.value = '';
    }

    const logo = logoUrl ? (
        <img
            src={logoUrl}
            alt={brandingCopy.logoAlt(companyName)}
            className="max-h-[58px] max-w-[124px] object-contain"
        />
    ) : (
        <MonoLabel size={10} tone="muted" variant="value">
            {editable ? brandingCopy.logoDropzone : companyName}
        </MonoLabel>
    );

    const frame =
        'flex h-[66px] w-[132px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] border bg-[image:var(--placeholder-hatch-light)]';

    const textOnAccent = effectiveTextOnAccent(draft);
    const ratio = contrastRatio(textOnAccent, draft.accent);
    const chosenTextUnreadable =
        draft.accentText !== null && !isReadable(draft.accentText, draft.accent);

    return (
        <Card padding="lg" as="section" aria-labelledby="branding-title">
            <h2
                id="branding-title"
                className="mb-[14px] font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {brandingCopy.title}
            </h2>

            <div className="flex flex-wrap items-start gap-[14px]">
                <div className="flex flex-col gap-2">
                    {editable ? (
                        <label
                            onDragOver={(event) => {
                                event.preventDefault();
                                setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                            aria-busy={uploading || undefined}
                            className={cn(
                                frame,
                                'cursor-pointer border-dashed',
                                'focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--accent)]',
                                dragging
                                    ? 'border-[var(--accent)]'
                                    : 'border-[var(--border-dashed)]',
                            )}
                        >
                            <input
                                type="file"
                                accept={LOGO_MIME_TYPES.join(',')}
                                className="sr-only"
                                aria-label={brandingCopy.logoUpload}
                                disabled={uploading}
                                onChange={handleChange}
                            />
                            {logo}
                        </label>
                    ) : (
                        <div className={cn(frame, 'border-[var(--border-default)]')}>{logo}</div>
                    )}

                    {editable && branding.logoUrl && !uploading ? (
                        <Button variant="ghost" size="xs" icon="delete" onClick={onLogoRemove}>
                            {brandingCopy.logoRemove}
                        </Button>
                    ) : null}

                    {editable ? (
                        <p className="max-w-[132px] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                            {uploading ? brandingCopy.logoUploading : brandingCopy.logoFormats}
                        </p>
                    ) : null}
                </div>

                <div className="flex min-w-[240px] flex-1 flex-col gap-[14px]">
                    <ColorRow
                        label={brandingCopy.accent}
                        hint={brandingCopy.accentHint}
                        value={draft.accent}
                        disabled={!canManage || savingHouseStyle}
                        onChange={(value) => change({ ...draft, accent: value })}
                    >
                        <div className="flex flex-wrap gap-2">
                            {BRAND_ACCENTS.map((swatch) => (
                                <button
                                    key={swatch.key}
                                    type="button"
                                    aria-label={swatch.label}
                                    aria-pressed={
                                        swatch.value.toLowerCase() === draft.accent.toLowerCase()
                                    }
                                    disabled={!canManage || savingHouseStyle}
                                    onClick={() => change({ ...draft, accent: swatch.value })}
                                    style={{
                                        background: swatch.cssVar,
                                        boxShadow:
                                            swatch.value.toLowerCase() ===
                                            draft.accent.toLowerCase()
                                                ? 'var(--shadow-focus)'
                                                : undefined,
                                    }}
                                    className={cn(
                                        'h-[30px] w-[30px] cursor-pointer rounded-[7px] border-0 outline-none disabled:cursor-default',
                                        focusRing,
                                    )}
                                />
                            ))}
                        </div>
                    </ColorRow>

                    <ColorRow
                        label={brandingCopy.accentText}
                        hint={brandingCopy.accentTextHint}
                        value={draft.accentText ?? textOnAccent}
                        disabled={!canManage || savingHouseStyle}
                        onChange={(value) => change({ ...draft, accentText: value })}
                    >
                        <AutoSwitch
                            automatic={draft.accentText === null}
                            automaticLabel={brandingCopy.automaticOn(
                                defaultTextOnAccent(draft.accent) === ON_ACCENT_LIGHT
                                    ? brandingCopy.white
                                    : brandingCopy.ink,
                            )}
                            onLabel={brandingCopy.automatic}
                            offLabel={brandingCopy.chooseOwn}
                            disabled={!canManage || savingHouseStyle}
                            onAutomatic={() => change({ ...draft, accentText: null })}
                            onOwn={() => change({ ...draft, accentText: textOnAccent })}
                        />
                    </ColorRow>

                    <p
                        role={chosenTextUnreadable ? 'alert' : undefined}
                        className={cn(
                            'flex flex-wrap items-center gap-2 text-[12px] leading-[1.45]',
                            chosenTextUnreadable
                                ? 'text-[var(--danger)]'
                                : 'text-[var(--text-tertiary-safe)]',
                        )}
                    >
                        <span
                            aria-hidden="true"
                            style={{ background: draft.accent, color: textOnAccent }}
                            className="rounded-[5px] px-2 py-[3px] text-[11px] font-semibold"
                        >
                            {companyName || brandingCopy.accent}
                        </span>
                        {chosenTextUnreadable
                            ? brandingCopy.contrastTooLow(ratio)
                            : brandingCopy.contrast(ratio)}
                        {chosenTextUnreadable ? (
                            <Button
                                variant="ghost"
                                size="xs"
                                disabled={!canManage || savingHouseStyle}
                                onClick={() =>
                                    change({
                                        ...draft,
                                        accentText: defaultTextOnAccent(draft.accent),
                                    })
                                }
                            >
                                {brandingCopy.contrastFix}
                            </Button>
                        ) : null}
                    </p>

                    <ColorRow
                        label={brandingCopy.heading}
                        hint={brandingCopy.headingHint}
                        value={draft.heading ?? ON_ACCENT_DARK}
                        disabled={!canManage || savingHouseStyle}
                        onChange={(value) => change({ ...draft, heading: value })}
                    >
                        <AutoSwitch
                            automatic={draft.heading === null}
                            automaticLabel={brandingCopy.standardOn}
                            onLabel={brandingCopy.standard}
                            offLabel={brandingCopy.chooseOwn}
                            disabled={!canManage || savingHouseStyle}
                            onAutomatic={() => change({ ...draft, heading: null })}
                            onOwn={() => change({ ...draft, heading: ON_ACCENT_DARK })}
                        />
                    </ColorRow>

                    <p className="text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                        {brandingCopy.note} {brandingCopy.statusNote}
                    </p>

                    {canManage ? (
                        <div>
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={!dirty || savingHouseStyle}
                                onClick={() => {
                                    setSaved(draft);
                                    onHouseStyleSave(draft);
                                }}
                            >
                                {savingHouseStyle ? brandingCopy.saving : brandingCopy.save}
                            </Button>
                        </div>
                    ) : (
                        <p className="text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                            {settingsCopy.readOnly}
                        </p>
                    )}

                    {notice ? (
                        <p
                            role="status"
                            className="text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                        >
                            {notice}
                        </p>
                    ) : null}

                    {(typeError ?? error) ? (
                        <p role="alert" className="text-[12px] leading-[1.4] text-[var(--danger)]">
                            {typeError ?? error}
                        </p>
                    ) : null}
                </div>
            </div>
        </Card>
    );
}

interface ColorRowProps {
    label: string;
    hint: string;
    /** Altijd een geldige `#RRGGBB`: de kiezer kent geen "leeg". */
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
    /** Wat er naast de kiezer staat: de swatches, of de schakelaar naar automatisch. */
    children?: ReactNode;
}

/**
 * Eén kleur: de kiezer met het volledige spectrum, en het veld waarin je zelf
 * een code typt.
 *
 * De kiezer is `input[type=color]` — het venster van het besturingssysteem, met
 * pipet en spectrum, zonder er een pakket bij te halen. Het tekstveld ernaast is
 * voor wie de code al heeft: `#1E5EFF`, `1e5eff` en `rgb(30, 94, 255)` gaan er
 * alle drie in. Wat je typt blijft staan tot het een kleur is; pas dan
 * verspringt de kiezer.
 */
function ColorRow({ label, hint, value, disabled, onChange, children }: ColorRowProps) {
    const [typed, setTyped] = useState<string | null>(null);
    const shown = typed ?? value;
    const invalid = typed !== null && parseColor(typed) === null;

    return (
        <div>
            <div className="mb-[6px] text-[12px] leading-[1.4] font-medium text-[var(--text-secondary)]">
                {label}
            </div>
            <p className="mb-[7px] text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                {hint}
            </p>

            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="color"
                    aria-label={brandingCopy.pick(label)}
                    value={value}
                    disabled={disabled}
                    onChange={(event) => {
                        setTyped(null);
                        onChange(event.target.value.toUpperCase());
                    }}
                    className={cn(
                        'h-[34px] w-[44px] cursor-pointer rounded-[7px] border border-[var(--border-control)] bg-[var(--surface-card)] p-[3px] disabled:cursor-default',
                        focusRing,
                    )}
                />

                <input
                    type="text"
                    inputMode="text"
                    spellCheck={false}
                    aria-label={brandingCopy.code(label)}
                    aria-invalid={invalid || undefined}
                    placeholder={brandingCopy.codePlaceholder}
                    value={shown}
                    disabled={disabled}
                    onChange={(event) => {
                        const next = event.target.value;
                        setTyped(next);

                        const parsed = parseColor(next);

                        if (parsed !== null) {
                            onChange(parsed);
                        }
                    }}
                    onBlur={() => setTyped(null)}
                    className={cn(
                        'h-[34px] w-[124px] rounded-[7px] border px-[10px] font-[family-name:var(--font-mono)] text-[13px] text-[var(--text-primary)] uppercase',
                        'bg-[var(--surface-card)] disabled:bg-[var(--surface-subtle)]',
                        invalid ? 'border-[var(--danger)]' : 'border-[var(--border-control)]',
                        focusRing,
                    )}
                />

                {children}
            </div>

            {invalid ? (
                <p role="alert" className="mt-1 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {brandingCopy.codeInvalid}
                </p>
            ) : null}
        </div>
    );
}

interface AutoSwitchProps {
    automatic: boolean;
    automaticLabel: string;
    onLabel: string;
    offLabel: string;
    disabled: boolean;
    onAutomatic: () => void;
    onOwn: () => void;
}

/**
 * "Laat de app kiezen" of "ik kies zelf", voor de twee kleuren die leeg mogen
 * blijven. Leeg is hier een echte keuze en geen vergeten veld, dus staat er wat
 * er dan gebeurt.
 */
function AutoSwitch({
    automatic,
    automaticLabel,
    onLabel,
    offLabel,
    disabled,
    onAutomatic,
    onOwn,
}: AutoSwitchProps) {
    return (
        <span className="flex items-center gap-2">
            {automatic ? (
                <span className="text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                    {automaticLabel}
                </span>
            ) : null}
            <Button
                variant="ghost"
                size="xs"
                disabled={disabled}
                onClick={automatic ? onOwn : onAutomatic}
            >
                {automatic ? offLabel : onLabel}
            </Button>
        </span>
    );
}
